'use client'

import * as React from 'react'

import { cn } from '../cn'
import { Badge } from './badge'

/**
 * Shared chrome for the action-card family: ChoiceCard, FormCard,
 * ConfirmCard, PreviewCard, DiffCard, StatusCard, ResultCard, PlanCard.
 *
 * Action cards are the "rich interactive surfaces" the agent renders
 * inline for tool calls — pick a slot, confirm a write, preview a draft.
 * They replace the old amber raw-JSON approval block with a structured,
 * branded surface composed from `@ordo/ui` primitives. Per
 * `apps/agent/plans/plan-action-cards.md`.
 *
 * The chrome is intentionally minimal — header eyebrow with optional
 * tool-slug attribution, content slot, optional footer. Each kind's
 * behavior lives in its own sibling primitive
 * (`action-card-<kind>.tsx`) and uses this as its outer frame.
 */

export type ActionCardTone = 'neutral' | 'pending' | 'destructive' | 'ok'

// Awaiting-input is the *normal* state of a card, not a warning — it
// renders neutral so it sits alongside the surrounding chat text
// instead of shouting amber. Destructive keeps a red edge but no
// body fill (the destructive button does the warning work). Resolved
// gets a thin emerald edge but no green flood, so a long transcript
// of resolved cards doesn't dominate when scrolled past. Per
// `apps/agent/plans/plan-action-cards-polish.md`.
const TONE_STYLES: Record<ActionCardTone, string> = {
  neutral: 'border-border bg-card',
  pending: 'border-border bg-card',
  destructive: 'border-destructive/40 bg-card',
  ok: 'border-emerald-500/30 bg-card',
}

export interface ActionCardProps {
  /** Headline shown in the card header. */
  title: string
  /** Optional sublabel under the title. */
  description?: string
  /**
   * Attribution badge in the top-right (e.g. "calendar · find_slots").
   * Tells the user this surface is rendered from a tool, not an
   * Ordo-native dialog — the trust/spoofing affordance.
   */
  attribution?: string
  /**
   * Visual tone. `pending` matches today's amber approval card so
   * awaiting-input surfaces are immediately recognizable; `destructive`
   * for irreversible writes; `ok` after resolution.
   */
  tone?: ActionCardTone
  /** Footer slot — typically a row of buttons. */
  footer?: React.ReactNode
  /** Card body content. */
  children?: React.ReactNode
  /** Extra classes appended to the outer card. */
  className?: string
}

export function ActionCard({
  title,
  description,
  attribution,
  tone = 'neutral',
  footer,
  children,
  className,
}: ActionCardProps) {
  return (
    <div
      className={cn('mt-3 overflow-hidden rounded-lg border text-sm shadow-sm', TONE_STYLES[tone], className)}
      role="region"
      aria-label={title}>
      <div className="flex items-start justify-between gap-3 px-3 pt-2.5 pb-1">
        <div className="min-w-0 flex-1">
          <div className="font-medium text-foreground">{title}</div>
          {description ? <div className="mt-0.5 text-xs text-muted-foreground">{description}</div> : null}
        </div>
        {attribution ? (
          <Badge variant="outline" className="shrink-0 font-mono text-[10.5px] font-normal">
            {attribution}
          </Badge>
        ) : null}
      </div>
      {children ? <div className="space-y-2 px-3 pt-1 pb-2.5">{children}</div> : null}
      {footer ? <div className="flex items-center gap-2 px-3 pb-2.5">{footer}</div> : null}
    </div>
  )
}
